"use client";

import { c as _c } from "react/compiler-runtime";
import { useBreakpoint } from "@dc/hooks";
import { useDisclosure } from "@dc/ui/components";
import Cookies from "js-cookie";
import { usePathname } from "next/navigation";
import { useTheme } from "next-themes";
import { useCallback, useEffect, useRef, useState } from "react";
import { NAV_COLLAPSE_COOKIE } from "@/hooks/navCollapseState.constants";
import { stripOrgTeamPrefix } from "@/lib/orgTeamPath";
import { HOME, type SidebarItem } from "../Sidebar/Sidebar.types";

/**
 * The breakpoint at which the sidebar (not just the drawer) should be shown.
 */
type SidebarBreakpoint = "hidden" | "sm" | "md" | "lg";
type UseNavigationSidebarProps = {
  navigationItems: SidebarItem[];
  selectedKey?: string;
  shouldCollapseNavOnMount?: boolean;
  sidebarBreakpoint?: SidebarBreakpoint;
};
const COLLAPSE_BREAKPOINT = 850;
export const useNavigationSidebar = t0 => {
  const $ = _c(44);
  const {
    navigationItems,
    selectedKey: propSelectedKey,
    shouldCollapseNavOnMount: t1,
    sidebarBreakpoint: t2
  } = t0;
  const shouldCollapseNavOnMount = t1 === undefined ? false : t1;
  const sidebarBreakpoint = t2 === undefined ? "sm" : t2;
  const {
    isOpen,
    onOpenChange,
    onClose
  } = useDisclosure();
  const [isCollapsed, setIsCollapsed] = useState(shouldCollapseNavOnMount);
  const hasStoredPreference = useRef(false);
  const [mounted, setMounted] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const scrollContainerRef = useRef(null);
  const pathname = usePathname();
  const breakpointForHook = sidebarBreakpoint === "hidden" ? "sm" : sidebarBreakpoint;
  const breakpointMatches = useBreakpoint(breakpointForHook);
  const {
    theme,
    setTheme,
    resolvedTheme
  } = useTheme();
  const isMobile = sidebarBreakpoint === "hidden" || (breakpointMatches === undefined ? false : !breakpointMatches);
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const stored = Cookies.get(NAV_COLLAPSE_COOKIE);
      if (stored !== undefined) {
        setIsCollapsed(stored === "true");
        hasStoredPreference.current = true;
      }
      setMounted(true);
    };
    t4 = [];
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      let debounceTimer;
      const checkOverflow = () => {
        if (scrollContainerRef.current) {
          const el = scrollContainerRef.current;
          const allChildren = el.querySelectorAll("*");
          let scrollableEl = null;
          for (const child of Array.from(allChildren)) {
            const htmlChild = child as HTMLElement;
            const computedStyle = window.getComputedStyle(htmlChild);
            const overflowY = computedStyle.overflowY;
            if ((overflowY === "auto" || overflowY === "scroll") && htmlChild.scrollHeight > htmlChild.clientHeight) {
              scrollableEl = htmlChild;
              break;
            }
          }
          if (!scrollableEl) {
            scrollableEl = el;
          }
          const isScrollable = scrollableEl.scrollHeight > scrollableEl.clientHeight || scrollableEl.scrollWidth > scrollableEl.clientWidth;
          setHasOverflow(isScrollable);
        }
      };
      const debouncedCheckOverflow = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(checkOverflow, 150);
      };
      const rafId = requestAnimationFrame(() => {
        checkOverflow();
      });
      window.addEventListener("resize", debouncedCheckOverflow);
      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(debounceTimer);
        window.removeEventListener("resize", debouncedCheckOverflow);
      };
    };
    t6 = [];
    $[2] = t5;
    $[3] = t6;
  } else {
    t5 = $[2];
    t6 = $[3];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[4] !== isMobile) {
    t7 = () => {
      let debounceTimer_0;
      const handleResponsiveCollapse = () => {
        if (isMobile) {
          return;
        }
        clearTimeout(debounceTimer_0);
        debounceTimer_0 = setTimeout(() => {
          const viewportWidth = window.innerWidth;
          setIsCollapsed(viewportWidth <= COLLAPSE_BREAKPOINT);
        }, 100);
      };
      if (!hasStoredPreference.current) {
        handleResponsiveCollapse();
      }
      hasStoredPreference.current = false;
      window.addEventListener("resize", handleResponsiveCollapse);
      return () => {
        clearTimeout(debounceTimer_0);
        window.removeEventListener("resize", handleResponsiveCollapse);
      };
    };
    t8 = [isMobile];
    $[4] = isMobile;
    $[5] = t7;
    $[6] = t8;
  } else {
    t7 = $[5];
    t8 = $[6];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[7] !== breakpointMatches || $[8] !== isMobile || $[9] !== onClose) {
    t9 = () => {
      if (breakpointMatches !== undefined) {
        if (isMobile) {
          setIsCollapsed(false);
        } else {
          onClose();
        }
      }
    };
    t10 = [isMobile, onClose, breakpointMatches];
    $[7] = breakpointMatches;
    $[8] = isMobile;
    $[9] = onClose;
    $[10] = t10;
    $[11] = t9;
  } else {
    t10 = $[10];
    t9 = $[11];
  }
  useEffect(t9, t10);
  let isSASettingsActive;
  let isSettingsActive;
  let strippedPath;
  let t11;
  if ($[12] !== pathname) {
    strippedPath = stripOrgTeamPrefix(pathname);
    isSASettingsActive = strippedPath.startsWith("/settings/admin");
    isSettingsActive = strippedPath.startsWith("/settings") && !isSASettingsActive;
    t11 = strippedPath.startsWith("/settings/team-settings");
    $[12] = pathname;
    $[13] = isSASettingsActive;
    $[14] = isSettingsActive;
    $[15] = strippedPath;
    $[16] = t11;
  } else {
    isSASettingsActive = $[13];
    isSettingsActive = $[14];
    strippedPath = $[15];
    t11 = $[16];
  }
  const isTeamSettingsActive = t11;
  const isOrgSettingsActive = isSettingsActive && !isTeamSettingsActive;
  let t12;
  if ($[17] !== isSettingsActive || $[18] !== navigationItems || $[19] !== propSelectedKey || $[20] !== strippedPath) {
    t12 = () => {
      if (isSettingsActive) {
        return;
      }
      if (propSelectedKey) {
        return propSelectedKey;
      }
      const match = [...navigationItems].filter(item => item.href && (strippedPath === item.href || strippedPath.startsWith(`${item.href}/`))).sort(_temp)[0];
      return match?.key || HOME;
    };
    $[17] = isSettingsActive;
    $[18] = navigationItems;
    $[19] = propSelectedKey;
    $[20] = strippedPath;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const getSelectedKey = t12;
  let t13;
  if ($[22] !== getSelectedKey) {
    t13 = getSelectedKey();
    $[22] = getSelectedKey;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const selectedKey = t13;
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setIsCollapsed(_temp2);
    };
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const onToggle = t14;
  let t15;
  if ($[25] !== resolvedTheme || $[26] !== setTheme) {
    t15 = () => {
      setTheme(resolvedTheme === "dark" ? "light" : "dark");
    };
    $[25] = resolvedTheme;
    $[26] = setTheme;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const toggleTheme = t15;
  const t16 = resolvedTheme || theme;
  let t17;
  if ($[28] !== hasOverflow || $[29] !== isCollapsed || $[30] !== isMobile || $[31] !== isOpen || $[32] !== isOrgSettingsActive || $[33] !== isSASettingsActive || $[34] !== isSettingsActive || $[35] !== isTeamSettingsActive || $[36] !== mounted || $[37] !== onClose || $[38] !== onOpenChange || $[39] !== selectedKey || $[40] !== sidebarBreakpoint || $[41] !== t16 || $[42] !== toggleTheme) {
    t17 = {
      isCollapsed,
      isMobile,
      isOpen,
      mounted,
      selectedKey,
      theme: t16,
      isSettingsActive,
      isOrgSettingsActive,
      isTeamSettingsActive,
      isSASettingsActive,
      hasOverflow,
      scrollContainerRef,
      sidebarBreakpoint,
      onOpenChange,
      onClose,
      onToggle,
      toggleTheme
    };
    $[28] = hasOverflow;
    $[29] = isCollapsed;
    $[30] = isMobile;
    $[31] = isOpen;
    $[32] = isOrgSettingsActive;
    $[33] = isSASettingsActive;
    $[34] = isSettingsActive;
    $[35] = isTeamSettingsActive;
    $[36] = mounted;
    $[37] = onClose;
    $[38] = onOpenChange;
    $[39] = selectedKey;
    $[40] = sidebarBreakpoint;
    $[41] = t16;
    $[42] = toggleTheme;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  return t17;
};
export type NavigationSidebarState = ReturnType<typeof useNavigationSidebar>;
function _temp(a, b) {
  return (b.href?.length ?? 0) - (a.href?.length ?? 0);
}
function _temp2(prev) {
  const newState = !prev;
  Cookies.set(NAV_COLLAPSE_COOKIE, newState ? "true" : "false", {
    path: "/",
    sameSite: "lax",
    expires: 365
  });
  return newState;
}